import type { FormEvent } from 'react'
import type { TFunction } from 'i18next'
import { CUIDARI_ACCESS_GATE_COPY } from '@francisquiniai/cuidari-core/auth'
import { cerberusAuthClient, CerberusError } from '@/lib/cerberus/client'
import type { useWaitlistGate } from '@/modules/waitlist/useWaitlistGate'
import { getReturnTo } from './shared'

export type LoginStage = 'credentials' | 'mfa' | 'reset'

export interface LoginActionDeps {
  t: TFunction<'auth'>
  gate: ReturnType<typeof useWaitlistGate>
  email: string
  password: string
  mfaCode: string
  challengeToken: string | null
  mfaUserId: string | null
  setBusy: (value: boolean) => void
  setError: (value: string | null) => void
  setNotice: (value: string | null) => void
  setChallengeToken: (value: string | null) => void
  setMfaUserId: (value: string | null) => void
  setMfaDeadline: (value: number | null) => void
  setMfaCode: (value: string) => void
  setStage: (value: LoginStage) => void
}

export async function submitCredentials(d: LoginActionDeps, e: FormEvent) {
  e.preventDefault()
  d.setBusy(true)
  d.setError(null)
  d.setNotice(null)
  try {
    const trimmedEmail = d.email.trim()
    const gateRes = await d.gate.check(trimmedEmail)
    if (!gateRes.allowed) {
      d.setNotice(CUIDARI_ACCESS_GATE_COPY.waitlistAccessDenied)
      return
    }
    const res = await cerberusAuthClient.login({
      email: trimmedEmail,
      password: d.password,
    })
    if (res.mfa_required) {
      if (!res.challenge_token || !res.user_id) {
        d.setError(
          d.t('login.errors.unexpected', {
            defaultValue: 'Resposta inesperada do servidor de autenticação',
          }),
        )
        return
      }
      d.setChallengeToken(res.challenge_token)
      d.setMfaUserId(res.user_id)
      const ttl = typeof res.expires_in === 'number' ? res.expires_in : 300
      d.setMfaDeadline(Date.now() + ttl * 1000)
      d.setMfaCode('')
      d.setStage('mfa')
    } else if (res.access_token && res.refresh_token) {
      const userId =
        res.user_id ??
        (res as unknown as { user?: { user_id?: string } }).user?.user_id
      if (!userId) {
        d.setError('Resposta inesperada do servidor.')
        return
      }
      const hash = `access_token=${encodeURIComponent(res.access_token)}&refresh_token=${encodeURIComponent(res.refresh_token)}&user_id=${encodeURIComponent(userId)}`
      window.location.assign(`${getReturnTo()}#${hash}`)
    } else {
      d.setError('Não foi possível autenticar.')
    }
  } catch (err) {
    d.setError(err instanceof Error ? err.message : 'E-mail ou senha incorretos.')
  } finally {
    d.setBusy(false)
  }
}

export async function submitMfa(d: LoginActionDeps, e: FormEvent) {
  e.preventDefault()
  d.setBusy(true)
  d.setError(null)
  if (!d.challengeToken || !d.mfaUserId) {
    d.setError(
      d.t('login.mfa.expired', {
        defaultValue: 'O desafio expirou. Refaça o login.',
      }),
    )
    d.setBusy(false)
    return
  }
  try {
    const res = await cerberusAuthClient.mfaChallenge({
      challenge_token: d.challengeToken,
      code: d.mfaCode.trim(),
      user_id: d.mfaUserId,
    })
    const userId = res.user_id ?? res.user?.user_id
    if (res.access_token && res.refresh_token && userId) {
      const hash = `access_token=${encodeURIComponent(res.access_token)}&refresh_token=${encodeURIComponent(res.refresh_token)}&user_id=${encodeURIComponent(userId)}`
      window.location.assign(`${getReturnTo()}#${hash}`)
    } else {
      d.setError(d.t('login.mfa.invalidCode', { defaultValue: 'Código MFA inválido' }))
    }
  } catch (err) {
    if (err instanceof CerberusError) {
      if (err.status === 429) {
        d.setError(
          d.t('login.mfa.rateLimited', {
            defaultValue: 'Muitas tentativas. Aguarde alguns segundos e tente de novo.',
          }),
        )
      } else if (err.code === 'invalid_challenge' || err.status === 400) {
        d.setError(
          d.t('login.mfa.expired', {
            defaultValue: 'O desafio expirou. Refaça o login.',
          }),
        )
      } else {
        d.setError(d.t('login.mfa.invalidCode', { defaultValue: 'Código MFA inválido' }))
      }
    } else {
      d.setError(d.t('login.mfa.invalidCode', { defaultValue: 'Código MFA inválido' }))
    }
  } finally {
    d.setBusy(false)
  }
}

export async function submitReset(d: LoginActionDeps, e: FormEvent) {
  e.preventDefault()
  d.setBusy(true)
  d.setError(null)
  d.setNotice(null)
  const trimmedEmail = d.email.trim()
  const returnTo =
    typeof window !== 'undefined' ? `${window.location.origin}/reset` : undefined
  try {
    await cerberusAuthClient.passwordResetRequest({
      email: trimmedEmail,
      ...(returnTo ? { return_to: returnTo } : {}),
    })
  } catch (err) {
    if (err instanceof CerberusError && err.status >= 500) {
      d.setError(err.message)
      d.setBusy(false)
      return
    }
  }
  d.setNotice('Se esse e-mail existir, você receberá um link para redefinir.')
  d.setBusy(false)
}
